import { FC, useEffect } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, Chip, Text } from '@frontend/design-system';
import { EIVDashboardTrialWidgetTrackingIds } from '../../../../constants/tracking-ids';
import { PayersList } from './payers-list';
import { WidgetLayout } from './widget-layout';

interface TrialActiveWidgetProps {
  containerStyles?: Interpolation<Theme>;
  remainingDays?: number;
  onUpgradeClick?: () => void;
}

export const TrialActiveWidget: FC<TrialActiveWidgetProps> = ({ containerStyles, remainingDays, onUpgradeClick }) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });

  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      pendo.track(EIVDashboardTrialWidgetTrackingIds.trialActiveWidgetVisible);
    }
  }, [inView]);

  return (
    <WidgetLayout
      containerStyles={containerStyles}
      headerActions={<ActiveTrialActions onUpgradeClick={onUpgradeClick} remainingDays={remainingDays} />}
      isAccordion={false}
      innerRef={ref}
      noBorder
    >
      <div css={defaultStyles.body}>
        <div css={defaultStyles.banner}>
          <Icon name='bulb-small' css={defaultStyles.bannerIcon} size={24} />
          <Text size='medium' weight='bold' css={defaultStyles.bannerText}>
            {t('Boost your success rate by completing your payer setup for deeper coverage insights')}
          </Text>
        </div>
        <PayersList />
      </div>
    </WidgetLayout>
  );
};

const ActiveTrialActions: FC<{ onUpgradeClick?: () => void; remainingDays?: number }> = ({
  onUpgradeClick,
  remainingDays,
}) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });

  return (
    <div css={defaultStyles.headerActions}>
      <Button
        variant='tertiary'
        onClick={onUpgradeClick}
        css={{ color: theme.colors.neutral50 }}
        trackingId={EIVDashboardTrialWidgetTrackingIds.headerUpgradeAction}
      >
        {t('Upgrade Now')}
      </Button>
      <Chip css={defaultStyles.daysRemainingChip}>{t('{{count}} days remaining', { count: remainingDays })}</Chip>
    </div>
  );
};

const defaultStyles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  headerActions: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  banner: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1)};
    border-radius: ${theme.borderRadius.small};
    width: fit-content;
  `,
  bannerIcon: css`
    flex-shrink: 0;
    padding: ${theme.spacing(0.5)};
    color: ${theme.colors.tangerine40};
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.full};
  `,
  bannerText: css`
    color: ${theme.colors.tangerine50};
  `,
  daysRemainingChip: css`
    max-width: max-content;
    background-color: ${theme.colors.white};
    color: ${theme.colors.secondary.seaweed50};
  `,
};
